import { useCallback, useEffect, useRef, useState } from "react";
import type { Artifact } from "@lumen/shared";
import { t } from "../../i18n";
import { api } from "../../api";
import { subscribeToDataChanges } from "../projects/projectEvents";
import { artifactVaultErrorMessage } from "./artifact-utils";

/**
 * Server-backed artifact list for one conversation. Fetches when first used
 * (and again when the revision changes), refreshes on data-changed
 * notifications, and exposes the load function for manual reloads. Results are
 * keyed by conversation so switching conversations never shows stale artifacts.
 */
export function useConversationArtifacts(
  conversationId: string | null | undefined,
  revision: string | number,
  active = true,
) {
  const [loaded, setLoaded] = useState<{
    conversationId: string;
    artifacts: Artifact[];
  } | null>(null);
  const [error, setError] = useState("");
  const inFlight = useRef<Promise<unknown> | null>(null);
  const load = useCallback(() => {
    if (!active || !conversationId) return;
    if (inFlight.current) return;
    const request = api<{ artifacts: Artifact[] }>(
      `/conversations/${encodeURIComponent(conversationId)}/artifacts`,
    )
      .then((response) => {
        setLoaded({
          conversationId,
          artifacts: Array.isArray(response?.artifacts)
            ? response.artifacts
            : [],
        });
        setError("");
      })
      .catch((cause) => {
        setLoaded({ conversationId, artifacts: [] });
        setError(
          artifactVaultErrorMessage(cause, t("Artifacts could not be loaded")),
        );
      })
      .finally(() => {
        if (inFlight.current === request) inFlight.current = null;
      });
    inFlight.current = request;
  }, [active, conversationId]);
  useEffect(() => {
    if (!active || !conversationId) {
      setLoaded(null);
      setError("");
      return;
    }
    let mounted = true;
    let debounce = 0;
    const schedule = () => {
      window.clearTimeout(debounce);
      debounce = window.setTimeout(() => {
        if (mounted) load();
      }, 300);
    };
    const unsubscribe = subscribeToDataChanges(schedule);
    load();
    return () => {
      mounted = false;
      window.clearTimeout(debounce);
      unsubscribe();
    };
  }, [active, conversationId, revision, load]);
  const artifacts =
    loaded && loaded.conversationId === conversationId
      ? loaded.artifacts
      : null;
  return { artifacts, loading: artifacts === null, error, reload: load };
}
